import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown } from 'react-icons/fa6';
import { CSVFileEntry } from '../../../../../types/common';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

interface ScalingSectionProps {
  entry: CSVFileEntry;
  isExpanded: boolean;
  onToggle: () => void;
  onInputChange: (field: keyof CSVFileEntry, value: string) => void;
  onRadioChange: (value: 'ja' | 'nein') => void;
}

/**
 * Skalierung section component
 * Displays scaling options with conditional min/max inputs when scaling is enabled
 */
export const ScalingSection: React.FC<ScalingSectionProps> = ({
  entry,
  isExpanded,
  onToggle,
  onInputChange,
  onRadioChange,
}) => {
  const { t } = useTranslation('training');

  const minValue = parseFloat(entry.skalierungMin || '0');
  const maxValue = parseFloat(entry.skalierungMax || '1');
  const isMinGreaterThanMax = entry.skalierung === 'ja' && !isNaN(minValue) && !isNaN(maxValue) && minValue > maxValue;

  const inputClasses = (hasError: boolean) => `w-full bg-card border rounded-lg px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 transition-shadow duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
    hasError
      ? 'border-red-300 focus:ring-red-500/30 focus:border-red-400'
      : 'border-input focus:ring-ring/30 focus:border-ring'
  }`;

  return (
    <div className="rounded-lg border border-border overflow-visible">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3.5 py-2.5 bg-muted/80 hover:bg-muted transition-colors duration-150"
      >
        <h3 className="text-xs font-medium text-foreground uppercase tracking-wide">
          {t('csvFileUploader.sections.scaling')}
        </h3>
        <FaChevronDown className={`w-2.5 h-2.5 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {isExpanded && (
        <div className="px-3.5 py-3 bg-card border-t border-border">
          {/* Scaling toggle */}
          <div className="mb-3">
            <div className="flex items-center gap-1 mb-1.5">
              <label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                {t('csvFileUploader.headers.skalierung')}
              </label>
              <InfoTooltip text={t('tooltips.config.skalierung')} width="w-64" align="start" />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name={`skalierung-${entry.id}`}
                  value="ja"
                  checked={entry.skalierung === 'ja'}
                  onChange={() => onRadioChange('ja')}
                  className="accent-primary"
                />
                <span className="text-xs text-foreground">{t('csvFileUploader.options.yes')}</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name={`skalierung-${entry.id}`}
                  value="nein"
                  checked={entry.skalierung === 'nein'}
                  onChange={() => onRadioChange('nein')}
                  className="accent-primary"
                />
                <span className="text-xs text-foreground">{t('csvFileUploader.options.no')}</span>
              </label>
            </div>
          </div>

          {/* Min/Max inputs */}
          {entry.skalierung === 'ja' && (
            <div className="pt-2.5 border-t border-border">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor={`csv-skalierungMin-${entry.id}`}
                    className="text-[10px] text-muted-foreground uppercase tracking-wide block mb-1"
                  >
                    {t('csvFileUploader.headers.skalierungMin')}
                  </label>
                  <input
                    id={`csv-skalierungMin-${entry.id}`}
                    type="number"
                    step="0.01"
                    placeholder={t('timeInformation.fields.min')}
                    value={entry.skalierungMin}
                    onChange={(e) => onInputChange('skalierungMin', e.target.value)}
                    className={inputClasses(isMinGreaterThanMax)}
                  />
                </div>

                <div>
                  <label
                    htmlFor={`csv-skalierungMax-${entry.id}`}
                    className="text-[10px] text-muted-foreground uppercase tracking-wide block mb-1"
                  >
                    {t('csvFileUploader.headers.skalierungMax')}
                  </label>
                  <input
                    id={`csv-skalierungMax-${entry.id}`}
                    type="number"
                    step="0.01"
                    placeholder={t('timeInformation.fields.max')}
                    value={entry.skalierungMax}
                    onChange={(e) => onInputChange('skalierungMax', e.target.value)}
                    className={inputClasses(isMinGreaterThanMax)}
                  />
                </div>
              </div>

              {isMinGreaterThanMax && (
                <p className="text-red-500 text-[10px] mt-1.5">
                  {t('csvFileUploader.validation.minGreaterThanMax')}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScalingSection;
